import { sub } from "date-fns";
import { generatePath } from "react-router-dom";

import { URLS } from "@/api/urls";
import { CreateSearchErrorResponse } from "@/types/CreateSearchErrorResponse";
import { FilterOptions } from "@/types/FilterOptions";
import { PaginatedMentionCards } from "@/types/PaginatedMentionCards";
import {
  CandidateSearch,
  Search,
  SearchLight,
  SearchPatchFields,
} from "@/types/Search";
import { SearchFilter, SearchFilterV4 } from "@/types/SearchFilter";
import { SearchListOptions } from "@/types/SearchListOptions";
import { TopWidgetData } from "@/types/TopWidgetData";
import { deleteNil, formatDateForApi } from "@/utils";

import fetcher_v2 from "../fetcher_v2";
import { Mention } from "@/types/Mention";
import { ComponentErrors } from "@/types/ComponentErrors";

/**
 * TODO:
 * - Add typings for all fetcher-calls, type all parameters properly
 * - Don't use 'any'
 */

export const getSearch = (uuid: Uuid) => {
  return fetcher_v2<Search>(generatePath(URLS.SEARCHES_DETAIL, { uuid }), {
    method: "GET",
  });
};

export const getSearchList = <T extends boolean>(
  search?: SearchListOptions<T>
) => {
  return fetcher_v2<Array<T extends true ? SearchLight : Search>>(
    URLS.SEARCHES_LIST,
    {
      body: search,
      method: "GET",
    }
  );
};

// Get's all the users searches that are currently active.
export const getOngoingSearches = () =>
  fetcher_v2<Search[]>(URLS.SEARCHES_LIST_ONGOING, { method: "GET" });

export const getSearchMentionsV4 = (
  uuid: Uuid,
  filters: SearchFilterV4,
  startDate?: Date
) => {
  return fetcher_v2<PaginatedMentionCards, SearchFilterV4>(
    generatePath(URLS.SEARCHES_DETAIL_MENTIONS_V4, { uuid }),
    {
      body: {
        start_date: formatDateForApi(
          startDate || sub(new Date(), { months: 3 })
        ),
        end_date: formatDateForApi(new Date()),
        includes: ["channel", "sentiment", "similar", "insights"],
        ...filters,
      },
    }
  );
};

export const getTagMentionsV4 = (
  slug: string,
  filters: SearchFilterV4,
  orgUuid?: Uuid
) => {
  return fetcher_v2<PaginatedMentionCards, SearchFilterV4>(
    generatePath(URLS.SEARCHES_DETAIL_MENTIONS_V4_TAG, { slug }),
    {
      body: {
        includes: ["channel", "sentiment", "similar", "insights"],
        orgUuid,
        ...filters,
      },
    }
  );
};

export const setSearchSeen = (uuid: Uuid) => {
  return fetcher_v2(
    generatePath(URLS.SEARCHES_UNREAD_NOTIFICATION_SEEN, { uuid }),
    { method: "POST" }
  );
};

export const refreshSearch = (uuid: Uuid) => {
  return fetcher_v2(generatePath(URLS.MENTIONS_RESET, { uuid }), {
    method: "POST",
  });
};

export const deleteSearch = (uuid: Uuid) => {
  return fetcher_v2(generatePath(URLS.SEARCHES_DETAIL, { uuid }), {
    method: "DELETE",
  });
};

export const updateSearchSortingPosition = (
  uuid: Uuid,
  sorting_position: number
) => {
  return fetcher_v2(generatePath(URLS.SEARCHES_DETAIL, { uuid }), {
    method: "PATCH",
    body: { sorting_position },
  });
};

export const updateFullSearch = (uuid: Uuid, body: SearchPatchFields) => {
  return fetcher_v2<Search, SearchPatchFields, ComponentErrors>(
    generatePath(URLS.SEARCHES_DETAIL, { uuid }),
    {
      method: "PUT",
      body,
    }
  );
};

export const getSearchFilterOptions = (uuid: Uuid) => {
  return fetcher_v2<FilterOptions>(
    generatePath(URLS.SEARCHES_FILTER_OPTIONS, { uuid }),
    {
      method: "GET",
    }
  );
};

// data {p: number}
export const getHiddenMentions = (uuid: Uuid, body = {}) => {
  return fetcher_v2<{ mentions: Mention[]; meta: { page_count: number } }>(
    generatePath(URLS.SEARCHES_HIDDEN_MENTIONS, { uuid }),
    {
      method: "GET",
      body,
    }
  );
};

export const markRelevant = (
  mention_uuids: string[],
  unhide_source?: boolean
) => {
  return fetcher_v2(URLS.MENTION_MARK_RELEVANT, {
    method: "PATCH",
    body: { mention_uuids, unhide_source },
  });
};

// body : {reason}
export const markIrrelevant = (
  mention_uuids: string[],
  { reason }: { reason: string | null } = { reason: null }
) => {
  return fetcher_v2(URLS.MENTION_MARK_IRRELEVANT, {
    method: "PATCH",
    body: { reason: reason, mention_uuids },
  });
};

export const hideIrrelevantMentions = (uuid: Uuid) => {
  return fetcher_v2(
    generatePath(URLS.MENTION_CARDS_HIDE_IRRELEVANT, { uuid }),
    { method: "POST" }
  );
};

export const getSearchTopWidgets = (
  uuid: Uuid,
  filters: SearchFilter,
  currency: string,
  signal?: AbortSignal
) =>
  fetcher_v2<TopWidgetData>(generatePath(URLS.SEARCHES_TOPWIDGETS, { uuid }), {
    method: "GET",
    body: {
      ...filters,
      currency,
    },
    signal,
  });

const prepareSearchForSubmit = (search: CandidateSearch) => ({
  ...deleteNil(search),
  queries: search.queries.map((q) => deleteNil(q)),
});

export const createSearch = (search: CandidateSearch) => {
  const body = prepareSearchForSubmit(search);

  return fetcher_v2<Search, typeof body, CreateSearchErrorResponse>(
    URLS.SEARCHES_LIST,
    {
      method: "POST",
      body,
    }
  );
};
